import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import conductorApi from '../../lib/conductorApi';
import { API_BASE } from '../../lib/api';
import { useConductorAuth } from '../../contexts/ConductorAuthContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, Calendar, Clock, User as UserIcon, Phone, Mail, Briefcase, Bus, FileText, CheckCircle2, XCircle, MessageSquare, Loader2, Car } from 'lucide-react';

const estadoStyles = {
  'Agendado':            { bg: 'bg-blue-100',   text: 'text-blue-700' },
  'Servicio Recibido':   { bg: 'bg-amber-100',  text: 'text-amber-800' },
  'Realizado':           { bg: 'bg-green-100',  text: 'text-green-700' },
  'Suspendido':          { bg: 'bg-red-100',    text: 'text-red-700' },
};

const formatearFechaCorta = (fecha) => {
  if (!fecha) return '-';
  try {
    const [y, m, d] = fecha.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return fecha; }
};
const formatearTimestamp = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const InfoRow = ({ icon: Icon, label, value, color = 'text-gray-400' }) => (
  value ? (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      <Icon size={16} className={`mt-0.5 shrink-0 ${color}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
      </div>
    </div>
  ) : null
);

export const ConductorServicioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { conductor } = useConductorAuth();
  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [empresaLogo, setEmpresaLogo] = useState(null);

  const [showRecepcion, setShowRecepcion] = useState(false);
  const [textoRecepcion, setTextoRecepcion] = useState('');
  const [showRealizado, setShowRealizado] = useState(false);
  const [showSuspender, setShowSuspender] = useState(false);
  const [nota, setNota] = useState('');
  const [nuevaBitacora, setNuevaBitacora] = useState('');
  const [savingBitacora, setSavingBitacora] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!conductor?.empresa_id) return;
    let active = true;
    let objectUrl = null;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/configuracion/logo?empresa_id=${conductor.empresa_id}&t=${Date.now()}`);
        if (resp.ok && active) {
          const blob = await resp.blob();
          objectUrl = URL.createObjectURL(blob);
          setEmpresaLogo(objectUrl);
        }
      } catch {}
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [conductor?.empresa_id]);

  const load = async () => {
    try {
      const response = await conductorApi.get(`/conductor/servicios/${id}`);
      setServicio(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar el servicio');
      navigate('/conductor');
    } finally {
      setLoading(false);
    }
  };

  const handleRecepcion = async () => {
    if (!textoRecepcion.trim()) {
      toast.error('Debe escribir un texto para confirmar la recepción');
      return;
    }
    setSubmitting(true);
    try {
      await conductorApi.post(`/conductor/servicios/${id}/recepcion`, { texto: textoRecepcion.trim() });
      toast.success('Servicio recibido — el administrador fue notificado');
      setShowRecepcion(false);
      setTextoRecepcion('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al confirmar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccion = async (tipo) => {
    setSubmitting(true);
    try {
      await conductorApi.post(`/conductor/servicios/${id}/${tipo}`, { nota: nota.trim() || null });
      toast.success(tipo === 'realizado' ? 'Servicio marcado como realizado' : 'Servicio suspendido');
      setShowRealizado(false);
      setShowSuspender(false);
      setNota('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgregarBitacora = async () => {
    const texto = nuevaBitacora.trim();
    if (!texto) {
      toast.error('La nota no puede estar vacía');
      return;
    }
    setSavingBitacora(true);
    try {
      await conductorApi.post(`/conductor/servicios/${id}/bitacora`, { texto });
      setNuevaBitacora('');
      toast.success('Nota agregada');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar nota');
    } finally {
      setSavingBitacora(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }
  if (!servicio) return null;

  const estilo = estadoStyles[servicio.estado] || estadoStyles['Agendado'];
  const recepcionConfirmada = !!servicio.conductor_recepcion_fecha;
  const finalizado = ['Realizado', 'Suspendido'].includes(servicio.estado);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-32">
      {/* Logo empresa */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-center">
          {empresaLogo ? (
            <img src={empresaLogo} alt="Logo empresa" className="h-10 w-auto object-contain" />
          ) : (
            <div className="h-10" />
          )}
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-30 shadow-sm">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/conductor')} className="p-2 -ml-2 text-gray-700 hover:text-primary flex items-center" data-testid="btn-back">
            <ChevronLeft size={22} />
            <span className="text-sm font-medium">Volver</span>
          </button>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${estilo.bg} ${estilo.text}`}>{servicio.estado}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Pasajero y fecha */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wide mb-2">
            <Calendar size={14} />
            <span className="capitalize">{formatearFechaCorta(servicio.fecha)}</span>
            <Clock size={14} className="ml-2" />
            <span>{servicio.hora} hrs</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Pasajero</p>
          <h2 className="text-xl font-bold text-gray-900 font-['Manrope'] leading-tight">
            {servicio.nombre_pasajero || servicio.pasajeros || 'Sin pasajero'}
          </h2>
          {servicio.telefono && (
            <a
              href={`tel:${servicio.telefono}`}
              className="text-sm text-primary font-medium mt-1 inline-flex items-center gap-1.5 hover:underline"
              data-testid="conductor-telefono"
            >
              <Phone size={13} /> {servicio.telefono}
            </a>
          )}
          {servicio.cliente_nombre && (
            <p className="text-sm text-gray-600 mt-2 flex items-center gap-1"><Briefcase size={12}/>{servicio.cliente_nombre}</p>
          )}
        </div>

        {/* Ruta */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-600 uppercase">Ruta</p>
            {servicio.tiene_retorno && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                Ida y vuelta
              </span>
            )}
          </div>
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-2 shrink-0"></div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Origen</p>
                <p className="text-sm font-medium text-gray-900">{servicio.origen}</p>
              </div>
            </div>
            {(servicio.desvios || []).map((d, idx) => (
              <div className="flex items-start gap-3" key={idx} data-testid={`conductor-desvio-${idx}`}>
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 mt-2 shrink-0"></div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Desvío {idx + 1}</p>
                  <p className="text-sm font-medium text-gray-900">{d}</p>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-2 shrink-0"></div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Destino</p>
                <p className="text-sm font-medium text-gray-900">{servicio.destino}</p>
              </div>
            </div>
            {servicio.tiene_retorno && (
              <div className="flex items-start gap-3 pt-1 border-t border-gray-100 mt-1">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-600 mt-2 shrink-0"></div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Retorno al origen</p>
                  <p className="text-sm font-medium text-gray-900">{servicio.origen}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Observaciones */}
        {servicio.observaciones && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase">Observaciones</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{servicio.observaciones}</p>
            </div>
          </div>
        )}

        {/* Bitácora del conductor */}
        {(servicio.conductor_recepcion_fecha || servicio.conductor_realizado_fecha || servicio.conductor_suspendido_fecha || (servicio.bitacoras_conductor && servicio.bitacoras_conductor.length > 0)) && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase">Bitácora</p>
            </div>
            <div className="px-4 py-3 space-y-3">
              {servicio.conductor_recepcion_fecha && (
                <div className="flex gap-3">
                  <div className="w-1 bg-amber-400 rounded-full shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-amber-600" />
                      <p className="text-sm font-semibold text-gray-900">Servicio Recibido</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{formatearTimestamp(servicio.conductor_recepcion_fecha)}</p>
                    {servicio.conductor_recepcion_texto && (
                      <p className="text-sm text-gray-700 mt-1 italic">“{servicio.conductor_recepcion_texto}”</p>
                    )}
                  </div>
                </div>
              )}
              {servicio.conductor_realizado_fecha && (
                <div className="flex gap-3">
                  <div className="w-1 bg-green-500 rounded-full shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-green-600" />
                      <p className="text-sm font-semibold text-gray-900">Servicio realizado</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{formatearTimestamp(servicio.conductor_realizado_fecha)}</p>
                    {servicio.conductor_realizado_nota && (
                      <p className="text-sm text-gray-700 mt-1 italic">“{servicio.conductor_realizado_nota}”</p>
                    )}
                  </div>
                </div>
              )}
              {servicio.conductor_suspendido_fecha && (
                <div className="flex gap-3">
                  <div className="w-1 bg-red-500 rounded-full shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <XCircle size={14} className="text-red-600" />
                      <p className="text-sm font-semibold text-gray-900">Servicio suspendido</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{formatearTimestamp(servicio.conductor_suspendido_fecha)}</p>
                    {servicio.conductor_suspendido_nota && (
                      <p className="text-sm text-gray-700 mt-1 italic">“{servicio.conductor_suspendido_nota}”</p>
                    )}
                  </div>
                </div>
              )}
              {(servicio.bitacoras_conductor || []).map((b, idx) => (
                <div className="flex gap-3" key={`bit-${idx}`}>
                  <div className="w-1 bg-primary rounded-full shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={14} className="text-primary" />
                      <p className="text-sm font-semibold text-gray-900">Nota del conductor</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{formatearTimestamp(b.fecha)}</p>
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{b.texto}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agregar nueva nota a la bitácora */}
        {servicio.estado !== 'Realizado' && servicio.estado !== 'Suspendido' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
              <MessageSquare size={14} className="text-primary" />
              <p className="text-xs font-semibold text-gray-600 uppercase">Agregar nota</p>
            </div>
            <div className="px-4 py-3 space-y-2">
              <Textarea
                value={nuevaBitacora}
                onChange={(e) => setNuevaBitacora(e.target.value)}
                placeholder="Ej: Pasajero atrasado 5 min, retomé ruta, evento en la calle..."
                rows={3}
                className="w-full resize-none text-sm"
                data-testid="nueva-bitacora-input"
              />
              <Button
                onClick={handleAgregarBitacora}
                disabled={savingBitacora || !nuevaBitacora.trim()}
                className="w-full"
                data-testid="agregar-bitacora-btn"
              >
                {savingBitacora ? 'Guardando...' : 'Guardar nota'}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Sticky bottom actions */}
      {!finalizado && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg z-40">
          <div className="max-w-2xl mx-auto">
            {!recepcionConfirmada ? (
              <Button onClick={() => setShowRecepcion(true)} className="w-full h-12 text-base" data-testid="btn-confirmar-recepcion">
                <MessageSquare size={18} className="mr-2" />
                Marcar Servicio Recibido
              </Button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => setShowSuspender(true)} variant="outline" className="h-12 text-sm border-red-200 text-red-700 hover:bg-red-50" data-testid="btn-suspender">
                  <XCircle size={16} className="mr-1.5" />
                  Servicio Suspendido
                </Button>
                <Button onClick={() => setShowRealizado(true)} className="h-12 text-sm bg-green-600 hover:bg-green-700" data-testid="btn-realizado">
                  <CheckCircle2 size={16} className="mr-1.5" />
                  Servicio Realizado
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Recepción */}
      <Dialog open={showRecepcion} onOpenChange={setShowRecepcion}>
        <DialogContent className="max-w-sm" data-testid="modal-recepcion">
          <DialogHeader>
            <DialogTitle>Marcar Servicio Recibido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Mensaje para el administrador *</Label>
            <Textarea
              value={textoRecepcion}
              onChange={(e) => setTextoRecepcion(e.target.value)}
              placeholder="Ej: Recibido, voy en camino al punto de recogida."
              rows={4}
              required
              data-testid="texto-recepcion-input"
            />
            <p className="text-xs text-gray-500">Este mensaje queda registrado con la hora actual y será visible para el administrador.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRecepcion(false)} className="flex-1">Cancelar</Button>
            <Button onClick={handleRecepcion} disabled={submitting || !textoRecepcion.trim()} className="flex-1" data-testid="submit-recepcion">
              {submitting ? 'Enviando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Realizado */}
      <Dialog open={showRealizado} onOpenChange={(v) => { setShowRealizado(v); if (!v) setNota(''); }}>
        <DialogContent className="max-w-sm" data-testid="modal-realizado">
          <DialogHeader>
            <DialogTitle>Confirmar Servicio Realizado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Nota opcional</Label>
            <Textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Comentario adicional (opcional)"
              rows={3}
              data-testid="nota-realizado-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRealizado(false)} className="flex-1">Cancelar</Button>
            <Button onClick={() => handleAccion('realizado')} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-realizado">
              {submitting ? 'Guardando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Suspender */}
      <Dialog open={showSuspender} onOpenChange={(v) => { setShowSuspender(v); if (!v) setNota(''); }}>
        <DialogContent className="max-w-sm" data-testid="modal-suspender">
          <DialogHeader>
            <DialogTitle>Suspender Servicio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Nota opcional</Label>
            <Textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Motivo de la suspensión (opcional)"
              rows={3}
              data-testid="nota-suspender-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSuspender(false)} className="flex-1">Cancelar</Button>
            <Button onClick={() => handleAccion('suspendido')} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="submit-suspender">
              {submitting ? 'Guardando...' : 'Suspender'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
